import React, { useState, useEffect } from 'react';
import { useFeathers } from '../../app/util';
import { ReactComponent as GreenCheckSvg } from '../../images/green-check.svg';
import {
  Button,
  Checkbox,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import SelectResearcherLevel from '../SelectResearcherLevel';
import { DistrictSelectInput } from '../DistrictSelectInput';

const permissionsAddOn = [
  {
    permission: 'research-measure',
    title: 'Research referendums'
  },
  {
    permission: 'publish-measure',
    title: 'Publish referendums'
  },
  {
    permission: 'office-descriptions',
    title: 'Create and edit offices'
  },
  {
    permission: 'dms-shapefile',
    title: 'Access shapefiles'
  },
  {
    permission: 'resolve-error',
    title: 'Resolve errors'
  }
]


const EditPermissionsModal = ( { user, refreshUsers, auditorMode = false }) => {
  const feathers = useFeathers()
  const [ userPermission, setUserPermission ] = useState(user.permissions[0])
  const [ researcherLevel, setResearcherLevel ] = useState(user?.researcherLevel);
  const [ publisherLevel, setPublisherLevel ] = useState(user?.publisherLevel);
  const [ saved, setSaved ] = useState(false)
  const [ error, setError ] = useState('')
  const [ isSuperAdmin, setIsSuperAdmin ] = useState(false)
  const [ limitedStates, setLimitedStates ] = useState([])

  const [ firstName, setFirstName ] = useState(user.firstName)
  const [ lastName, setLastName ] = useState(user.lastName)
  const [ publicBio, setPublicBio ] = useState(user.publicBio || '')
  const [ publicRole, setPublicRole ] = useState(user.publicRole || '')
  const [ isPublicTeamMember, setIsPublicTeamMember ] = useState(Boolean(user.isPublicTeamMember))

  const [ additionalPermissions, setAdditionalPermissions ] = useState(user.permissions.filter((p) => permissionsAddOn.map(({ permission }) => permission).includes(p)))

  const isTargetEditorPlus = (user.permissions || []).some((p) => ['editor', 'super-admin'].includes(p));

  useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const { user } = await feathers.get('authentication');
        setIsSuperAdmin(
          user.permissions.includes('super-admin') || 
          user.permissions.includes('editor')
        );
      } catch (error) {
        console.error('Error checking super-admin status:', error);
      }
    };
    
    checkSuperAdmin();
  }, [feathers]);

  const editPermissions = async () => {
    try {
      await feathers.getService('users').patch(user._id, {
        permissions: [ userPermission, ...(additionalPermissions || []) ],
        firstName: firstName,
        lastName: lastName,
        ...(!auditorMode ? { isPublicTeamMember } : {}),
        ...(isTargetEditorPlus && isSuperAdmin ? { publicBio, publicRole } : {}),
        ...(!auditorMode && researcherLevel ? { researcherLevel: parseInt(researcherLevel) } : {}),
        ...(!auditorMode && publisherLevel ? { publisherLevel: parseInt(publisherLevel) } : {}),
        ...(auditorMode ? { limitAccessToStates: limitedStates.map(state => state._id || state) } : {})
      })
      setSaved(true)
      refreshUsers()
    } catch (err) {
      setError(err.message)
    }
  }

  const fetchStates = async () => {
    const states = await feathers.getService('districts').find({
      query: {
        _id: { $in: user.limitAccessToStates },
        $limit: user.limitAccessToStates.length
      }
    })
    setLimitedStates(states.data.map(state => ({ ...state, text: state.longName })))
  }

  useEffect(() => {
    if(auditorMode && user?.limitAccessToStates?.length) {
      fetchStates()
    }
  }, [auditorMode, user])

  return (<>
    <EditPermissionsModalWrapper>
      <Typography variant="h1">
        Manage user
      </Typography>
      <UserDetailsWrapper>
        <Typography style={{ paddingTop: '4px' }}>
          First name
        </Typography>
        <TextField
          variant="outlined"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          size="small"
          fullWidth
        />
        <Typography style={{ paddingTop: '4px' }}>
          Last name
        </Typography>
        <TextField
          variant="outlined"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          size="small"
          fullWidth
        />
        <Typography>Email</Typography>
        <Typography>{user.email}</Typography>
        {!auditorMode && (
          <>
            <Typography style={{ marginTop: '40px' }}>Access Level</Typography>
            <FormControl style={{ marginTop: '30px' }} fullWidth size="small">
              <Select
                value={userPermission || ''}
                onChange={(event) => setUserPermission(event.target.value)}
              >
                <MenuItem value="researcher">Researcher</MenuItem>
                <MenuItem value="reviewer">Reviewer</MenuItem>
                {isSuperAdmin && <MenuItem value="editor">Editor</MenuItem>}
                <MenuItem value="publisher">Publisher</MenuItem>
              </Select>
            </FormControl>
            <Typography style={{ marginTop: '20px' }}>Researcher Level</Typography>
            <SelectResearcherLevel
              style={{ marginTop: '12px' }}
              onChange={(e) => {
                setResearcherLevel(e.target.value)
              }}
              value={researcherLevel}
              row
              name="researcher-level"
            />
            <Typography style={{ marginTop: '8px' }}>Publisher Level</Typography>
            <SelectResearcherLevel
              onChange={(e) => {
                setPublisherLevel(e.target.value)
              }}
              value={publisherLevel}
              row
              name="publisher-level"
            />
            <Typography style={{ marginTop: '12px' }}>Additional Permissions</Typography>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridColumnGap: '16px', gridRowGap: '8px' }}>
              {
                permissionsAddOn.map(({ permission, title }) => (
                  <div key={permission} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <Checkbox 
                      checked={additionalPermissions.includes(permission)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAdditionalPermissions([...additionalPermissions, permission])
                        } else {
                          setAdditionalPermissions(additionalPermissions.filter((p) => p !== permission))
                        }
                      }}
                    />
                    <Typography variant='body1'>{title}</Typography>
                  </div>
                ))
              }
            </div>
          </>
        )}
        {auditorMode && (
          <>
            <Typography style={{ marginTop: '8px' }}>Limit Access to States</Typography>
            <DistrictSelectInput
              value={limitedStates}
              onChange={e => setLimitedStates(e.target.value)}
              districtTypes={['state']}
              multi={true}
              style={{ width: '100%' }}
            />
          </>
        )}
        {!auditorMode && (
          <>
            <Typography style={{ marginTop: '8px' }}>Show on About page</Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                checked={isPublicTeamMember}
                onChange={(e) => setIsPublicTeamMember(e.target.checked)}
              />
              <Typography variant='body1'>Public team member</Typography>
            </div>
          </>
        )}
        {!auditorMode && isTargetEditorPlus && isSuperAdmin && (
          <>
            <Typography style={{ marginTop: '8px' }}>Public Role</Typography>
            <TextField
              variant="outlined"
              value={publicRole}
              onChange={(e) => setPublicRole(e.target.value)}
              size="small"
              fullWidth
            />
            <Typography style={{ marginTop: '8px' }}>Public Bio</Typography>
            <TextField
              variant="outlined"
              value={publicBio}
              onChange={(e) => setPublicBio(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={4}
            />
          </>
        )}
      </UserDetailsWrapper>
      { !saved
        ?
          <Button
            onClick={editPermissions}
            variant='contained'
            color={'primary'}
          >
            Save
          </Button>
        :
          <SavedWrapper>
            <GreenCheck/>
            <Typography>Changes saved for { user.email }.</Typography>
          </SavedWrapper>
      }
      {
        error && <Typography style={{ color: 'red' }}>{error}</Typography>
      }
    </EditPermissionsModalWrapper>
  </>);
}

const EditPermissionsModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 60px;
  justify-content: space-between;
  gap: 24px;
  min-width: 320px;

  .MuiTypography-h1 {
    font-size: 22px;
    line-height: 1.2;
  }
`

const GreenCheck = styled(GreenCheckSvg)`
  width: 25px;
  height: auto;
`

const SavedWrapper = styled.div`
  display: flex;
  gap: 10%;
  justify-content: center;
  align-items: center;
`

const UserDetailsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  grid-column-gap: 30px;
  grid-row-gap: 16px;
`

  export default EditPermissionsModal;